// App.js
import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import Popup from "./Popup";
import { formatDateTime, normalizeServerHtml } from "./utils";
import { initApiService, getFormattedSummary, getCaseNamesApi, getCaseInfo as svcGetCaseInfo, createAndSaveJournal, autoSaveEditedSummaryApi } from "./services/apiService";
import { initTranscriptService, getTranscript as svcGetTranscript } from "./services/transcriptService";
import { initWsService, connectWebSocket as wsConnectWebSocket, closeWebSocket } from "./services/wsService";

function App() {
  const [, setStatus] = useState("Not connected");
  const [, setCallId] = useState("");

  // App.js
  const [isSummaryGenerated, setIsSummaryGenerated] = useState(false);

  const [contactId, setContactId] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [summary, setSummary] = useState("");
  const [agentNotes, setAgentNotes] = useState("");

  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState("");

  const [callStatus, setCallStatus] = useState(null);
  const [liveCallActive, setLiveCallActive] = useState(false);
  const [callStartTime, setCallStartTime] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLiveCall, setIsLiveCall] = useState(false);
  const [journalId, setJournalId] = useState(null);
  const [caseNumberList, setCaseNumberList] = useState([]);
  // Ref so WebSocket handler always sees the latest value without stale closure
  const caseNumberListRef = useRef([]);


  const [activeTab, setActiveTab] = useState("transcript");

  const [initialCaseNumber, setInitialCaseNumber] = useState("");
  const [initialCaseName, setInitialCaseName] = useState("");
  const [obEnv, setObEnv] = useState(process.env.REACT_APP_OB_ENV || "");
  const [agentExtension, setAgentExtension] = useState("");
  const [staffId, setStaffId] = useState("");
  const [parentOrigin, setParentOrigin] = useState("");


  const [wsConnectingStatus, setWsConnectingStatus] = useState(null); // null | 'connecting' | 'connected'
  const wsTimeoutRef = useRef(null);

  const pingIntervalRef = useRef(null);
  const wsRef = useRef(null);

  // Track current contactId to ensure we append (never replace) for same contact
  const currentContactIdRef = useRef(null);

  const [accessToken, setAccessToken] = useState("");
  const [, setRefreshToken] = useState("");

  const accessTokenRef = useRef("");
  const refreshTokenRef = useRef("");

  // initialize apiService with refs so refresh/authorizedFetch use same tokens
  useEffect(() => {
    initApiService({ accessRef: accessTokenRef, refreshRef: refreshTokenRef });
  }, []);

  // Keep ref in sync whenever state changes
  useEffect(() => {
    caseNumberListRef.current = caseNumberList;
  }, [caseNumberList]);

  // initialize transcriptService with callbacks
  useEffect(() => {
    initTranscriptService({
      setLoadingTranscript,
      setTranscript,
      setSummary,
      setContactId,
      setCallStartTime,
      setLastUpdated,
      setCallStatus,
      setIsLiveCall,
      setJournalId,
      setIsSummaryGenerated,
      setCaseNumberList: (raw) => {
        const normalized = normalizeCaseNumberList(raw);
        setCaseNumberList(normalized);
      },
      setAgentNotes,
      onInitWS: connectWebSocket,
    });
  }, []);

  // initialize wsService with callbacks
  useEffect(() => {
    initWsService({
      setStatus,
      setLiveCallActive,
      onTranscriptMessage: handleIncomingTranscript,
      onWsOpen: () => setWsConnectingStatus('connected'),
    });

    // cleanup on unmount
    return () => {
      closeWebSocket();
      if (wsTimeoutRef.current) clearTimeout(wsTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper: base64 decode safely
  function safeBase64Decode(value, label) {
    if (!value) return "";
    try {
      const decoded = atob(value);
      return decoded;
    } catch (err) {
      return value;
    }
  }

useEffect(() => {
  const params = new URLSearchParams(window.location.search);

  const accessParam = params.get("access_token");
  const refreshParam = params.get("refresh_token");
  const callIdB64 = params.get("callId");
  const caseNumberB64 = params.get("caseNumber");
  const caseNameB64 = params.get("caseName");
  const obEnvParam = params.get("obEnv");
  const staffIdB64 = params.get("staffId");
  const hostParam = params.get("host");

  if (accessParam) {
    setAccessToken(accessParam);
    accessTokenRef.current = accessParam;
  }

  if (refreshParam) {
    setRefreshToken(refreshParam);
    refreshTokenRef.current = refreshParam;
  }

  if (staffIdB64) {
    try {
      const decoded = atob(staffIdB64);
      setStaffId(decoded);
    } catch (err) {
      // ignore
    }
  }

  if (callIdB64) {
    const decodedCallId = safeBase64Decode(callIdB64, "callId");
    setCallId(decodedCallId);
    initializeCallFlow(decodedCallId);
  }

  if (caseNumberB64) {
    const decodedUrl = decodeURIComponent(caseNumberB64);   // ← STEP 1
    const decodedCase = safeBase64Decode(decodedUrl, "caseNumber"); // ← STEP 2
    setInitialCaseNumber(decodedCase);
  }

  if (caseNameB64) {
    const decodedUrl = decodeURIComponent(caseNameB64);     // ← STEP 1
    const decodedCaseName = safeBase64Decode(decodedUrl, "caseName"); // ← STEP 2
    setInitialCaseName(decodedCaseName);
  }

  if (obEnvParam) {
    setObEnv(obEnvParam);
  }

  if (hostParam) {
    // host may arrive base64-encoded or as a plain URL — try decoded first
    let resolvedHost = hostParam;
    try {
      const decoded = atob(decodeURIComponent(hostParam));
      if (decoded.startsWith("http://") || decoded.startsWith("https://")) {
        resolvedHost = decoded;
      }
    } catch {
      // not base64 — use raw value as-is
    }
    // postMessage targetOrigin must be just the origin (scheme+host+port), no path
    try {
      resolvedHost = new URL(resolvedHost).origin;
    } catch {
      // if URL parsing fails, use as-is
    }
    setParentOrigin(resolvedHost);
  }

  // 🚀 Remove params from URL
  window.history.replaceState({}, "", window.location.pathname);

  const wsNode = wsRef.current;
  const pingNode = pingIntervalRef.current;
  return () => {
    if (wsNode) {
        try {
          wsNode.close();
        } catch {}
    }
    if (pingNode) clearInterval(pingNode);
  };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);



  // Normalize CaseNumberList from backend — may arrive as string, array, or object
  function normalizeCaseNumberList(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === "string") {
      // comma-separated string e.g. "12345,67890"
      return raw.split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (typeof raw === "object") {
      // object whose keys are case numbers e.g. { "12345": "name" }
      return Object.keys(raw).filter(Boolean);
    }
    return [];
  }

  async function initializeCallFlow(callIdParam) {
    try {
      // ❗ Don't force COMPLETED here; let getTranscript decide
      await svcGetTranscript(callIdParam, { accessToken, agentExtension, setAgentExtension });
    } catch (err) {
      setTranscript("No transcript available");
    }
  }

  // connectWebSocket now comes from wsService
  function connectWebSocket(callIdParam, initPayload) {
    setWsConnectingStatus('connecting');

    // If no transcript message arrives within 30s, show "No transcript available"
    if (wsTimeoutRef.current) clearTimeout(wsTimeoutRef.current);
    wsTimeoutRef.current = setTimeout(() => {
      setWsConnectingStatus(null);
      setTranscript((prev) => prev || "No transcript available");
    }, 30000);

    wsConnectWebSocket(callIdParam, initPayload);
  }

  function handleIncomingTranscript(d, callIdParam) {
    // Clear the connecting status and 30s timeout on first message
    setWsConnectingStatus(null);
    if (wsTimeoutRef.current) {
      clearTimeout(wsTimeoutRef.current);
      wsTimeoutRef.current = null;
    }

    const calls = d.Calls || {};
    const contact = d.ContactId || callIdParam;
    const startTime = d.call_start_time || calls.call_start_time || null;
    const lastTime = d.last_updated || calls.last_updated || null;
    if (d.journalId) {
      setJournalId(d.journalId);
    }

    if (typeof d.isSummaryGenerated === "boolean") {
      setIsSummaryGenerated(d.isSummaryGenerated);
    }

    if (d.Notes != null) {
      setAgentNotes(d.Notes);
    }

    if (d.CaseNumberList != null) {
      const normalized = normalizeCaseNumberList(d.CaseNumberList);
      setCaseNumberList(normalized);
      caseNumberListRef.current = normalized;
    }

    // Determine call status correctly
    const raw = d.call_status || calls.call_status || "ACTIVE";
    const normalized = raw.trim().toUpperCase();

    setCallStatus(normalized);

    // Completed? -> disable live mode
    if (normalized === "COMPLETED" || normalized === "FINISHED") {
      setLiveCallActive(false);
      setIsLiveCall(false);
    } else {
      setLiveCallActive(true);
      setIsLiveCall(true);
    }

    let transcriptText = "";
    if (Array.isArray(d.transcript) && d.transcript.length) {
      transcriptText = d.transcript
        .map((item) => {
          const ts = item.timestamp
            ? formatTimestamp(item.timestamp)
            : null;
          return ts
            ? `[${ts}] ${item.role}: ${item.text}`
            : `${item.role}: ${item.text}`;
        })
        .join("\n");
    } else if (typeof d.Transcript === "string") {
      transcriptText = d.Transcript;
    } else {
      transcriptText = "No transcript available";
    }

    // Check if contactId changed
    const isDifferentContact = currentContactIdRef.current && currentContactIdRef.current !== contact;

    if (isDifferentContact) {
      // New contact -> replace transcript and clear notes
      currentContactIdRef.current = contact;
      setContactId(contact);
      setTranscript(transcriptText);
      setCallStartTime(formatDateTime(startTime));
      setLastUpdated(formatDateTime(lastTime));
      setAgentNotes(d.Notes || "");
    } else {
      // Same contact or first time: ALWAYS append (never replace)
      currentContactIdRef.current = contact;
      setContactId(contact);

      // Always append new WS text, even if transcript state is not yet updated
      setTranscript((prev) => {
        if (!transcriptText || transcriptText === "No transcript available") {
          return prev; // ignore empty/placeholder messages
        }
        // Append to existing transcript (whether from REST or prior WS)
        const incoming = transcriptText.trim();
        if (!incoming) return prev;

        if (prev && prev.trim()) {
          // avoid adding extra blank line if prev already ends with newline
          const prevEndsWithNL = /\n\s*$/.test(prev);
          const sep = prevEndsWithNL ? "" : "\n";
          const combined = `${prev}${sep}${incoming}`;
          // collapse 3+ newlines down to 2 for readability
          return combined.replace(/\n{3,}/g, "\n\n");
        } else {
          // First message for this contact (prev is empty) -> set it (trimmed)
          return incoming;
        }
      });

      setLastUpdated(lastTime);
    }
  }

  async function getSummary() {
    if (!contactId) return;
    setLoadingSummary(true);
    try {
      // Step 1 — fetch case names if we have case numbers
      let caseNamesPayload = null;
      const wsNumbers = caseNumberListRef.current;
      // Also include the case number from query params (if not already in the list)
      const currentCaseNumbers = (initialCaseNumber && !wsNumbers.includes(initialCaseNumber))
        ? [...wsNumbers, initialCaseNumber]
        : wsNumbers;
      if (currentCaseNumbers && currentCaseNumbers.length > 0) {
        try {
          const raw = await getCaseNamesApi(currentCaseNumbers, obEnv);

          // Filter to only valid cases (those that have at least one person)
          const validCases = (raw?.cases || []).filter(
            c => c.persons && c.persons.length > 0
          );

          // Only attach payload if at least one valid case exists
          if (validCases.length > 0) {
            caseNamesPayload = { cases: validCases };
          }
        } catch (err) {
          // proceed without case names
        }
      }

      // Step 2 — fetch summary, forwarding case names in the payload
      const html = await getFormattedSummary(contactId, caseNamesPayload);
      setSummary(normalizeServerHtml(html));
      setIsSummaryGenerated(true);
    } catch (err) {
      // ignore
    } finally {
      setLoadingSummary(false);
    }
  }

  // delegate to service (returns { caseName, raw })
  async function getCaseInfo(caseNumber) {
    try {
      return await svcGetCaseInfo(caseNumber, obEnv);
    } catch (err) {
      return null;
    }
  }

  // delegate create+save journal to service
  async function createJournal(payload) {
    return await createAndSaveJournal({
      caseNumber: payload.caseNumber,
      shortDescription: payload.shortDescription,
      longDescription: payload.longDescription,
      contactId: payload.contactId,
      agentExtension: agentExtension || "N/A",
      staffId: staffId || "N/A",
      obEnv,
    });
  }

  // Helper timestamp formatter
  function formatTimestamp(isoString) {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], { hour12: false });
    } catch {
      return isoString;
    }
  }

  // use service wrapper
  // Failures propagate to the caller: the summary editor needs them to show the
  // "Auto-save failed" chip and to keep the editor open on a failed manual save
  // instead of reporting a success that never reached the server.
  async function autoSaveEditedSummary(longDescription) {
    await autoSaveEditedSummaryApi({
      initialCaseNumber,
      initialCaseName,
      longDescription,
      contactId,
      journalId,
    });
  }

 // RENDER – popup UI only
  return (
    <div className="main-bg" style={{ height: "100vh" }}>
      <Popup
        transcript={transcript}
        contactId={contactId}
        callStartTime={callStartTime}
        lastUpdated={lastUpdated}
        summary={summary}
        tab={activeTab}
        loadingTranscript={loadingTranscript}
        loadingSummary={loadingSummary}
        isEditing={isEditing}
        editedSummary={editedSummary}
        setEditedSummary={setEditedSummary}
        setIsEditing={setIsEditing}
        setSummary={setSummary}
        getSummary={getSummary}
        liveCallActive={liveCallActive}
        callStatus={callStatus}
        isLiveCall={isLiveCall}
        onTabChange={(tab) => setActiveTab(tab)}
        initialCaseNumber={initialCaseNumber}
        initialCaseName={initialCaseName}
        obEnv={obEnv}
        staffId={staffId}
        getCaseInfo={getCaseInfo}
        isSummaryGenerated={isSummaryGenerated}
        onSubmitJournal={async (journalPayload) => {
          return await createJournal({
            caseNumber: journalPayload.caseNumber,
            shortDescription: journalPayload.shortDescription,
            longDescription: journalPayload.longDescription,
            contactId: journalPayload.contactId,
            caseName: journalPayload.caseName,
          });
        }}
        journalId={journalId}
        onAutoSaveEditedSummary={autoSaveEditedSummary}
        wsConnectingStatus={wsConnectingStatus}
        parentOriginOverride={parentOrigin}
        notes={agentNotes}
      />
    </div>
  );
}

export default App;
